import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Card, CardContent, Avatar, IconButton, Button, Chip, TextField, List, ListItem, ListItemText, Divider, Badge, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Person as PersonIcon, PhoneIphone as PhoneIcon, Save as SaveIcon, Groups as GroupsIcon, AdminPanelSettings as AdminIcon, Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import AvatarCustomization from './AvatarCustomization';

const SettingsPage: React.FC = () => {
  const { user, userSites, refresh: refreshUser, logout } = useUser();
  const navigate = useNavigate();
  const [editName, setEditName] = useState<string>('');
  const [isSavingName, setIsSavingName] = useState<boolean>(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState<boolean>(false);
  const [viewAvatarOpen, setViewAvatarOpen] = useState<boolean>(false);

  useEffect(() => {
    setEditName(user?.participant?.name || '');
  }, [user?.participant?.name]);

  // Format phone number for display (e.g., +852 6732 2298)
  const formatPhoneNumber = (phone: string | undefined) => {
    if (!phone) return '—';
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // If it starts with 852, format as +852 XXXX XXXX
    if (digits.startsWith('852') && digits.length === 11) {
      return `+852 ${digits.slice(3, 7)} ${digits.slice(7)}`;
    }
    
    // If it's 8 digits, assume HK local number
    if (digits.length === 8) {
      return `+852 ${digits.slice(0, 4)} ${digits.slice(4)}`;
    }
    
    // Return as-is if format doesn't match
    return phone;
  };

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>設定</Typography>
        <Card variant="outlined">
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 28,
                    height: 28,
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                  onClick={() => setAvatarDialogOpen(true)}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              }
            >
              <Avatar 
                src={user?.avatarType === 'image' && user?.avatarUrl ? user.avatarUrl : undefined}
                sx={{ 
                  bgcolor: user?.avatarType === 'image' ? '#ffffff' : (user?.avatarColor || 'primary.main'),
                  border: '3px solid',
                  borderColor: user?.avatarType === 'image' ? 'divider' : 'transparent',
                  width: 72,
                  height: 72,
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: user?.avatarType === 'image' ? 'primary.main' : 'transparent',
                  }
                }}
                onClick={() => setViewAvatarOpen(true)}
              >
                {user?.avatarType === 'initial' && user?.avatarInitial 
                  ? user.avatarInitial
                  : (user?.participant?.name || user?.username || 'U').charAt(0).toUpperCase()
                }
              </Avatar>
            </Badge>
            <Box sx={{ flex: 1 }}>
              {/* Username (for admins) */}
              {user?.username && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    用戶名稱（用於登入）
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {user.username}
                  </Typography>
                </Box>
              )}
              
              {/* Display Name */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  顯示名稱（參加活動時顯示）
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <PersonIcon fontSize="small" color="action" />
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    placeholder="輸入參加活動的顯示名稱"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                </Box>
              </Box>
              
              {/* Phone Number */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  電話號碼
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">{formatPhoneNumber(user?.phoneNumber || user?.participant?.phoneNumber)}</Typography>
                </Box>
              </Box>
            </Box>
            <IconButton
              sx={{ position: 'absolute', right: 8, bottom: 8 }}
              color="primary"
              disabled={isSavingName || (editName || '').trim() === (user?.participant?.name || '')}
              onClick={async () => {
                const next = (editName || '').trim();
                if (!next) return;
                try {
                  setIsSavingName(true);
                  const token = localStorage.getItem('token');
                  await axios.put(`${API_BASE_URL}/auth/profile`, { name: next }, { headers: { Authorization: `Bearer ${token}` } });
                  await refreshUser();
                } finally {
                  setIsSavingName(false);
                }
              }}
              aria-label="儲存名稱"
            >
              <SaveIcon />
            </IconButton>
          </CardContent>
        </Card>

        {/* Groups Section */}
        {userSites && userSites.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <GroupsIcon color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  我的群組
                </Typography>
              </Box>
              
              {/* Owned Groups */}
              {userSites.filter(s => s.role === 'owner').length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <AdminIcon fontSize="small" />
                    擁有的群組 ({userSites.filter(s => s.role === 'owner').length})
                  </Typography>
                  <List dense disablePadding>
                    {userSites
                      .filter(s => s.role === 'owner')
                      .map((site, index) => (
                        <ListItem 
                          key={site._id}
                          sx={{ 
                            pl: 0, 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            borderRadius: 1
                          }}
                          onClick={() => navigate(`/${site.route}`)}
                        >
                          <ListItemText 
                            primary={site.name}
                            secondary={`/${site.route}`}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                          <Chip label="場主" size="small" color="primary" />
                        </ListItem>
                      ))}
                  </List>
                </>
              )}

              {/* Managed Groups (Admin) */}
              {userSites.filter(s => s.role === 'admin').length > 0 && (
                <>
                  {userSites.filter(s => s.role === 'owner').length > 0 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <AdminIcon fontSize="small" />
                    管理的群組 ({userSites.filter(s => s.role === 'admin').length})
                  </Typography>
                  <List dense disablePadding>
                    {userSites
                      .filter(s => s.role === 'admin')
                      .map((site, index) => (
                        <ListItem 
                          key={site._id}
                          sx={{ 
                            pl: 0, 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            borderRadius: 1
                          }}
                          onClick={() => navigate(`/${site.route}`)}
                        >
                          <ListItemText 
                            primary={site.name}
                            secondary={`/${site.route}`}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                          <Chip label="管理員" size="small" color="default" />
                        </ListItem>
                      ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card variant="outlined" sx={{ borderColor: 'error.light' }}>
          <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2">登出</Typography>
              <Typography variant="caption" color="text.secondary">登出你的帳戶</Typography>
            </Box>
            <Button variant="outlined" color="error" onClick={() => { logout(); navigate('/'); }}>登出</Button>
          </CardContent>
        </Card>
      </Box>

      {/* Profile Picture View Modal */}
      <Dialog 
        open={viewAvatarOpen} 
        onClose={() => setViewAvatarOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          個人頭像
          <IconButton onClick={() => setViewAvatarOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Avatar 
              src={user?.avatarType === 'image' && user?.avatarUrl ? user.avatarUrl : undefined}
              sx={{ 
                bgcolor: user?.avatarType === 'image' ? '#ffffff' : (user?.avatarColor || 'primary.main'),
                border: '3px solid',
                borderColor: user?.avatarType === 'image' ? 'divider' : 'transparent',
                width: 200,
                height: 200,
                fontSize: '4rem',
              }}
            >
              {user?.avatarType === 'initial' && user?.avatarInitial 
                ? user.avatarInitial
                : (user?.participant?.name || user?.username || 'U').charAt(0).toUpperCase()
              }
            </Avatar>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewAvatarOpen(false)}>
            關閉
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setViewAvatarOpen(false);
              setAvatarDialogOpen(true);
            }}
          >
            編輯頭像
          </Button>
        </DialogActions>
      </Dialog>

      {/* Avatar Customization Dialog */}
      <AvatarCustomization
        open={avatarDialogOpen}
        onClose={() => setAvatarDialogOpen(false)}
        currentInitial={user?.avatarInitial || (user?.participant?.name || user?.username || 'U').charAt(0).toUpperCase()}
        currentAvatarType={user?.avatarType}
        currentAvatarUrl={user?.avatarUrl}
        currentAvatarColor={user?.avatarColor}
        onSave={async () => {
          await refreshUser();
          setAvatarDialogOpen(false);
        }}
      />
    </Container>
  );
};

export default SettingsPage;


